import React, { useId, useState } from 'react';
import { ChevronDown, Sparkles } from 'lucide-react';
import { ReadOnlyValue } from '../../../components/ui';
import { inputShell } from './ContextQuestionCard';
import type { BomAnswer, ExactPart } from './useContextStep';

/**
 * What Robbie read off the BOM instead of asking, shown at the foot of each
 * component's section rather than as one long list of its own. Collapsed by
 * default: a gradient "Robbie already filled N specs" line marks it as Robbie's work,
 * and a preview of the values sits beside it so a wrong read can be spotted
 * without opening anything. Opened, each answer is editable and carries the
 * words it came from so the buyer can see why Robbie believes it.
 */

const sourceLine = (answer: BomAnswer): string | null => {
  if (!answer.evidence) return null;
  return answer.source === 'catalogue' ? answer.evidence : `From your BOM: “${answer.evidence}”`;
};

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** The collapsed "Robbie did this" row and the panel it opens. */
const RobbieDisclosure: React.FC<{ summary: string; preview: string; children: React.ReactNode }> = ({
  summary,
  preview,
  children,
}) => {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-s3 bg-ai-chip px-s6 py-[11px] text-left transition-colors hover:brightness-[0.98]"
      >
        <Sparkles className="h-[14px] w-[14px] shrink-0 text-robbie" aria-hidden />
        <span className="shrink-0 bg-ai-text bg-[length:200%_100%] bg-clip-text text-row-title text-transparent motion-safe:animate-ai-glint">
          {summary}
        </span>
        {!open && <span className="min-w-0 flex-1 truncate font-mono text-meta text-ink-muted">{preview}</span>}
        <ChevronDown
          className={`ml-auto h-4 w-4 shrink-0 text-robbie-ink transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>
      {open && (
        <div id={panelId} className="border-t border-robbie-border-soft px-s6 py-[12px]">
          {children}
        </div>
      )}
    </div>
  );
};

export interface BomAnswersDisclosureProps {
  answers: BomAnswer[];
  values: Record<string, unknown>;
  onChange: (field: string, value: string) => void;
  readOnly: boolean;
}

/** One component's BOM-read answers, pre-filled and editable once opened. */
export const BomAnswersDisclosure: React.FC<BomAnswersDisclosureProps> = ({ answers, values, onChange, readOnly }) => {
  const valueOf = (answer: BomAnswer) => String(values[answer.field] ?? answer.value);
  return (
    <RobbieDisclosure
      summary={`Robbie already filled ${plural(answers.length, 'spec', 'specs')}`}
      preview={answers.map(valueOf).join(' · ')}
    >
      <p className="m-0 mb-s4 text-meta text-ink-muted">I read these from your rows instead of asking. Change any I got wrong.</p>
      <div className="flex flex-col gap-s4">
        {answers.map((answer) => (
          <div key={answer.field} className="grid grid-cols-1 items-start gap-s2 sm:grid-cols-[minmax(120px,220px)_1fr] sm:gap-s4">
            <label htmlFor={`bom-answer-${answer.field}`} className="text-body-sm text-ink-secondary sm:pt-[8px]">
              {answer.label}
            </label>
            <div className="min-w-0">
              {readOnly ? (
                <ReadOnlyValue value={valueOf(answer)} />
              ) : (
                <div className={inputShell}>
                  <input
                    id={`bom-answer-${answer.field}`}
                    type="text"
                    value={valueOf(answer)}
                    onChange={(e) => onChange(answer.field, e.target.value)}
                    className="w-full border-none bg-transparent p-0 font-sans text-body-md text-ink outline-none"
                  />
                </div>
              )}
              {sourceLine(answer) && (
                <p className="m-0 mt-[2px] truncate text-meta text-ink-muted">{sourceLine(answer)}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </RobbieDisclosure>
  );
};

/** Rows that name an exact part: nothing was asked of them, so they get the same collapsed line. */
export const ExactPartsDisclosure: React.FC<{ parts: ExactPart[] }> = ({ parts }) => (
  <RobbieDisclosure
    summary={`Robbie is sourcing ${plural(parts.length, 'part', 'parts')} exactly as named`}
    preview={parts.map((p) => p.partNumber).join(' · ')}
  >
    <ul className="m-0 list-none p-0">
      {parts.map((part) => (
        <li key={part.componentKey} className="text-body-sm text-ink-secondary">
          <span className="font-mono text-ink">{part.partNumber}</span> · {part.componentName}
        </li>
      ))}
    </ul>
    <p className="m-0 mt-s2 text-meta text-ink-muted">The part number fixes every spec, so I didn't ask about these.</p>
  </RobbieDisclosure>
);
